import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo, LogoMark } from '@/components/Logo';

const HERO_IMG = 'https://images.unsplash.com/photo-1518766492976-82c449b7d616?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, User, MapPin } from 'lucide-react';

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register, user, loading } = useAuth();
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    commune: '' 
  });

  useEffect(() => {
    if (user) {
      navigate('/feed', { replace: true });
    }
  }, [user, navigate]);

  const communes = [
    // Nord
    { id: 'Saint-Denis', name: 'Saint-Denis' },
    { id: 'Sainte-Marie', name: 'Sainte-Marie' },
    { id: 'Sainte-Suzanne', name: 'Sainte-Suzanne' },
    // Est
    { id: 'Saint-André', name: 'Saint-André' },
    { id: 'Bras-Panon', name: 'Bras-Panon' },
    { id: 'Saint-Benoît', name: 'Saint-Benoît' },
    { id: 'Sainte-Rose', name: 'Sainte-Rose' },
    { id: 'Plaine-des-Palmistes', name: 'La Plaine-des-Palmistes' },
    // Sud
    { id: 'Saint-Pierre', name: 'Saint-Pierre' },
    { id: 'Saint-Louis', name: 'Saint-Louis' },
    { id: 'Saint-Joseph', name: 'Saint-Joseph' },
    { id: 'Saint-Philippe', name: 'Saint-Philippe' },
    { id: 'Petite-Île', name: 'Petite-Île' },
    { id: 'Le-Tampon', name: 'Le Tampon' },
    { id: 'Entre-Deux', name: 'Entre-Deux' },
    { id: 'Cilaos', name: 'Cilaos' },
    { id: 'Étang-Salé', name: "L'Étang-Salé" },
    { id: 'Les-Avirons', name: 'Les Avirons' },
    // Ouest
    { id: 'Saint-Paul', name: 'Saint-Paul' },
    { id: 'Saint-Leu', name: 'Saint-Leu' },
    { id: 'Trois-Bassins', name: 'Trois-Bassins' },
    { id: 'La-Possession', name: 'La Possession' },
    { id: 'Le-Port', name: 'Le Port' },
    // Cirque
    { id: 'Salazie', name: 'Salazie' }
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login(loginForm.email, loginForm.password);
    
    if (result.success) {
      toast.success('Bienvenue sur Bourbon !');
      navigate('/feed', { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerForm.commune) {
      toast.error('Veuillez sélectionner votre commune');
      return;
    }

    const result = await register(
      registerForm.email,
      registerForm.password,
      registerForm.name,
      registerForm.commune
    );
    
    if (result.success) {
      toast.success('Compte créé ! Bienvenue sur Bourbon !');
      navigate('/feed', { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/feed';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[1.1fr_1fr]" data-testid="auth-page">
      {/* Visual panel */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden p-10 text-white grain">
        <img src={HERO_IMG} alt="La Réunion" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(218_35%_9%)] via-[hsl(218_35%_9%/0.55)] to-[hsl(218_35%_9%/0.2)]" />
        <div className="relative z-10 flex items-center justify-between">
          <Logo />
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-secondary mb-4">Lo réseau social péi</p>
          <h2 className="text-4xl font-extrabold leading-tight mb-4">Ton quartier, en direct, par ceux qui y vivent.</h2>
          <p className="text-white/75 leading-relaxed">Alertes, événements et bons plans dans les 24 communes de La Réunion.</p>
        </div>
      </aside>

      {/* Form panel */}
      <div className="flex flex-col min-h-screen tropic-bg">
        <header className="flex items-center justify-between p-4 lg:hidden">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn-mobile">
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
          <Logo size="sm" />
        </header>

        <main className="flex-1 flex items-center justify-center p-5 sm:p-10">
          <div className="w-full max-w-md animate-fade-in">
            <div className="mb-8">
              <span className="hidden lg:block mb-6"><LogoMark className="h-12 w-12" /></span>
              <h1 className="text-3xl font-extrabold mb-2">Bienvenue sur Bourbon</h1>
              <p className="text-muted-foreground">Connecte-toi pour rejoindre ta commune.</p>
            </div>

            <Button
              variant="outline"
              className="w-full mb-6 py-6 rounded-2xl bg-card"
              onClick={handleGoogleAuth}
              data-testid="google-auth-btn"
            >
              <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuer avec Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="tropic-bg px-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ou par e-mail</span></div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 h-12 rounded-2xl bg-muted/70 p-1">
                <TabsTrigger value="login" className="rounded-xl font-semibold data-[state=active]:shadow-md" data-testid="login-tab">Connexion</TabsTrigger>
                <TabsTrigger value="register" className="rounded-xl font-semibold data-[state=active]:shadow-md" data-testid="register-tab">Inscription</TabsTrigger>
              </TabsList>

              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="votre@email.re"
                        className="pl-10 h-12 rounded-xl bg-card"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10 h-12 rounded-xl bg-card"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-2xl py-6 text-base font-semibold shadow-lg shadow-primary/25 btn-bounce"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <span className="spinner mr-2" /> : null}
                    Se connecter
                  </Button>
                </form>
              </TabsContent>

              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Nom complet</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Jean-Pierre Dupont"
                        className="pl-10 h-12 rounded-xl bg-card"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="votre@email.re"
                        className="pl-10 h-12 rounded-xl bg-card"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10 h-12 rounded-xl bg-card"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                        minLength={6}
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-commune">Commune</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                      <Select 
                        value={registerForm.commune}
                        onValueChange={(value) => setRegisterForm({ ...registerForm, commune: value })}
                      >
                        <SelectTrigger className="pl-10 h-12 rounded-xl bg-card" data-testid="register-commune-select">
                          <SelectValue placeholder="Sélectionnez votre commune" />
                        </SelectTrigger>
                        <SelectContent>
                          {communes.map((commune) => (
                            <SelectItem key={commune.id} value={commune.id}>
                              {commune.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-2xl py-6 text-base font-semibold shadow-lg shadow-primary/25 btn-bounce"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? <span className="spinner mr-2" /> : null}
                    Créer mon compte
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AuthPage;
